import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Modal, Spinner } from '../../../components/ui';
import { usePermissions } from '../../../hooks/usePermissions';
import { locationsService } from '../../../services/locationsService';
import { materialCertificateService } from '../../../services/materialCertificateService';
import type { MovementType } from '../../../services/stockMovementService';
import type { MaterialCertificate, SiteWithLocations, StorageBin, StorageLocation } from '../types';
import { certificateHeadline, certificatesByBin } from '../utils/certificates';
import { googleMapsUrl, ledgerPath, siteEyebrow, siteMetaLabel, siteTitle } from '../utils/siteDrillThrough';
import AdjustInventoryModal from './AdjustInventoryModal';
import AttachCertificateModal from './AttachCertificateModal';
import type { CertificatesLoad } from './BinCertificates';
import BinFormModal from './BinFormModal';
import { LOCATIONS_MANAGE_PERMISSION, STRUCTURE_NO_PERMISSION_REASON } from '../utils/structureForms';
import LocationBinsCard from './LocationBinsCard';
import OwnerPill from './OwnerPill';
import SiteFormModal from './SiteFormModal';
import StorageLocationFormModal from './StorageLocationFormModal';
import StartCountModal from './StartCountModal';

/** Server and UI use the same keys; see roles-permissions.js. */
export const ADJUST_PERMISSION = 'materials:adjust';
export const COST_MANAGE_PERMISSION = 'materials:cost_manage';
export const CERTIFICATES_PERMISSION = 'materials:certificates_manage';

/** Which structure form is open: the site's details, a location, or a bin in a location. */
type StructureForm =
  | { kind: 'site' }
  | { kind: 'location'; location: StorageLocation | null }
  | { kind: 'bin'; locationId: string; bin: StorageBin | null };

/** Why Adjust and Count are switched off. Shown under the buttons, not only on hover. */
export const NO_PERMISSION_REASON = 'You do not have permission to adjust inventory.';
export const NO_BINS_REASON = 'This site has no bins to adjust yet.';
export const COUNT_POST_PERMISSION = 'materials:count_post';
export const NO_COUNT_PERMISSION_REASON = 'You do not have permission to count materials.';
export const NO_STOCKED_BINS_REASON = 'No bin at this site holds a material yet.';

interface AdjustRequest {
  type: MovementType;
  binId: string | null;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'missing' }
  | { status: 'ready'; site: SiteWithLocations };

interface SiteDetailModalProps {
  /** The site to show; null keeps the modal closed. */
  siteId: string | null;
  onHide: () => void;
  /** Something changed stock or certificates here, so the list behind should refresh its flags. */
  onChanged?: () => void;
}

/**
 * One site, opened over the Locations list the way QP Tracking opens a task: its storage
 * locations, each location's bins, what every bin holds and how it stands against its band,
 * and the certificates on each bin. Anyone holding materials:adjust can record a movement
 * against a bin from here.
 */
const SiteDetailModal: React.FC<SiteDetailModalProps> = ({ siteId, onHide, onChanged }) => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const canAdjust = hasPermission(ADJUST_PERMISSION);
  const canManageCost = hasPermission(COST_MANAGE_PERMISSION);
  const canManageCertificates = hasPermission(CERTIFICATES_PERMISSION);
  const canManageStructure = hasPermission(LOCATIONS_MANAGE_PERMISSION);
  const [structureForm, setStructureForm] = useState<StructureForm | null>(null);
  const openStructure = (form: StructureForm) => {
    setNotice(null);
    setStructureForm(form);
  };
  const canCount = hasPermission(COUNT_POST_PERMISSION);
  const [counting, setCounting] = useState(false);

  const [load, setLoad] = useState<LoadState>({ status: 'loading' });
  const [adjust, setAdjust] = useState<AdjustRequest | null>(null);
  const [notice, setNotice] = useState<{ variant: 'success' | 'warning'; text: string } | null>(null);
  const [certificates, setCertificates] = useState<{ load: CertificatesLoad; list: MaterialCertificate[] }>({
    load: 'loading',
    list: [],
  });
  const [attachFor, setAttachFor] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<MaterialCertificate | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const fetchSite = useCallback(() => {
    if (!siteId) return () => undefined;
    let cancelled = false;
    setLoad({ status: 'loading' });
    locationsService
      .getSite(siteId)
      .then((site) => {
        if (!cancelled) setLoad(site ? { status: 'ready', site } : { status: 'missing' });
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Could not load this site.' });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  /**
   * Certificates load beside the site rather than inside it: a failure here leaves the bins
   * and the Adjust form usable, and says so on each bin instead of failing the whole view.
   */
  const fetchCertificates = useCallback(async () => {
    if (!siteId) return false;
    try {
      const list = await materialCertificateService.listForSite(siteId);
      setCertificates({ load: 'ready', list });
      return true;
    } catch {
      setCertificates((current) => ({ ...current, load: 'error' }));
      return false;
    }
  }, [siteId]);

  useEffect(() => {
    setNotice(null);
    setCertificates({ load: 'loading', list: [] });
    const cancel = fetchSite();
    void fetchCertificates();
    return cancel;
  }, [fetchSite, fetchCertificates]);

  /** Reload after a save without the spinner, so the user keeps their place. */
  const refreshAfterSave = async (message: string) => {
    if (!siteId) return;
    try {
      const site = await locationsService.getSite(siteId);
      if (site) setLoad({ status: 'ready', site });
      setNotice({ variant: 'success', text: message });
    } catch {
      setNotice({ variant: 'warning', text: `${message} The balances could not be refreshed. Reopen the site to see them.` });
    }
    onChanged?.();
  };

  const refreshCertificates = async (message: string) => {
    const ok = await fetchCertificates();
    setNotice(
      ok ? { variant: 'success', text: message } : { variant: 'warning', text: `${message} The certificate list could not be refreshed.` }
    );
  };

  const confirmRemove = async () => {
    if (!removeTarget || removing) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await materialCertificateService.remove(removeTarget.id);
      const headline = certificateHeadline(removeTarget.description);
      setRemoveTarget(null);
      await refreshCertificates(`Removed ${headline}.`);
    } catch (error) {
      setRemoveError(error instanceof Error ? error.message : 'Failed to remove the certificate');
    } finally {
      setRemoving(false);
    }
  };

  const openAdjust = (request: AdjustRequest) => {
    setNotice(null);
    setAdjust(request);
  };
  const adjustBin = (bin: StorageBin) => openAdjust({ type: bin.material && bin.quantity > 0 ? 'CONSUME' : 'RECEIPT', binId: bin.id });

  const byBin = useMemo(() => certificatesByBin(certificates.list), [certificates.list]);
  const site = load.status === 'ready' ? load.site : null;
  const hasBins = site?.locations.some((location) => location.bins.length > 0) ?? false;
  // One reason at a time, permission first: it is the one the user cannot fix themselves.
  const disabledReason = !canAdjust ? NO_PERMISSION_REASON : !hasBins ? NO_BINS_REASON : null;
  // A count lists the bins holding a material; an empty bin has nothing to compare against.
  const stockedBins = site?.locations.reduce((sum, location) => sum + location.bins.filter((bin) => bin.material).length, 0) ?? 0;
  const countReason = !canCount ? NO_COUNT_PERMISSION_REASON : !stockedBins ? NO_STOCKED_BINS_REASON : null;
  const removeBinLabel = removeTarget && site
    ? (() => {
        const location = site.locations.find((l) => l.bins.some((b) => b.id === removeTarget.binId));
        return location ? `${location.name} · ${removeTarget.binCode}` : removeTarget.binCode;
      })()
    : '';

  return (
    <>
      <Modal show={siteId !== null} onHide={onHide} size="3xl" tabletVariant="fullscreen" mobileVariant="fullscreen">
        <Modal.Header closeButton>
          <div className="tw-min-w-0">
            {site ? (
              <p className="tw-mb-0.5 tw-font-display tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-content-muted">
                {siteEyebrow(site.type)}
              </p>
            ) : null}
            <Modal.Title>{site ? siteTitle(site) : 'Site'}</Modal.Title>
            {site?.description ? <p className="tw-mb-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">{site.description}</p> : null}
          </div>
        </Modal.Header>

        <Modal.Body className="tw-space-y-4 tw-bg-surface-alt">
          {load.status === 'loading' ? (
            <div className="tw-flex tw-justify-center tw-py-10">
              <Spinner className="tw-text-primary">Loading site</Spinner>
            </div>
          ) : load.status === 'missing' ? (
            <Alert variant="light">
              <p className="tw-mb-1 tw-font-semibold">Site not found</p>
              This site does not exist, or it is not in your service area.
            </Alert>
          ) : load.status === 'error' ? (
            <Alert variant="danger">
              <p className="tw-mb-2">{load.message}</p>
              <Button size="sm" variant="outline-danger" onClick={fetchSite}>
                Try again
              </Button>
            </Alert>
          ) : (
            <>
              {notice ? (
                <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)} className="tw-mb-0">
                  {notice.text}
                </Alert>
              ) : null}

              <Card>
                <Card.Body className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-2 tw-py-3">
                  <OwnerPill owner={load.site.owner} fallback="Unknown owner" />
                  <span className="tw-text-sm tw-text-content-secondary">{siteMetaLabel(load.site)}</span>
                  {load.site.latitude != null && load.site.longitude != null ? (
                    <a
                      href={googleMapsUrl(load.site.latitude, load.site.longitude)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-text-sm tw-text-primary hover:tw-underline"
                    >
                      <i className="bi bi-geo-alt tw-mr-1" aria-hidden="true" />
                      {load.site.latitude.toFixed(5)}, {load.site.longitude.toFixed(5)}
                    </a>
                  ) : null}
                  <Button variant="link" size="sm" className="tw-ml-auto tw-p-0" onClick={() => navigate(ledgerPath({ siteId: load.site.id }))}>
                    <i className="bi bi-clock-history" aria-hidden="true" />
                    Ledger history
                  </Button>
                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => openStructure({ kind: 'site' })}
                      disabled={!canManageStructure}
                      disabledReason={canManageStructure ? undefined : STRUCTURE_NO_PERMISSION_REASON}
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                      Edit site
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => openStructure({ kind: 'location', location: null })}
                      disabled={!canManageStructure}
                      disabledReason={canManageStructure ? undefined : STRUCTURE_NO_PERMISSION_REASON}
                    >
                      <i className="bi bi-plus-lg" aria-hidden="true" />
                      Add storage location
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              {load.site.locations.length > 0 ? (
                load.site.locations.map((location) => (
                  <LocationBinsCard
                    key={location.id}
                    location={location}
                    onAdjust={canAdjust ? adjustBin : undefined}
                    onHistory={(bin) => navigate(ledgerPath({ siteId: load.site.id, binId: bin.id }))}
                    structure={
                      canManageStructure
                        ? {
                            onEditLocation: () => openStructure({ kind: 'location', location }),
                            onAddBin: () => openStructure({ kind: 'bin', locationId: location.id, bin: null }),
                            onEditBin: (bin) => openStructure({ kind: 'bin', locationId: location.id, bin }),
                          }
                        : {}
                    }
                    certificates={{
                      byBin,
                      load: certificates.load,
                      onAttach: canManageCertificates
                        ? (bin) => {
                            setNotice(null);
                            setAttachFor(bin.id);
                          }
                        : undefined,
                      onRemove: canManageCertificates
                        ? (certificate) => {
                            setRemoveError(null);
                            setRemoveTarget(certificate);
                          }
                        : undefined,
                    }}
                  />
                ))
              ) : (
                <Card>
                  <Card.Body className="tw-text-center tw-text-content-muted">
                    <p className="tw-mb-0 tw-font-semibold tw-text-content">No locations recorded for this site yet</p>
                    {canManageStructure ? (
                      <>
                        <p className="tw-mb-3 tw-mt-1 tw-text-sm">Add a shed, stockpile or tank, then the bins inside it.</p>
                        <Button size="sm" variant="primary" onClick={() => openStructure({ kind: 'location', location: null })}>
                          <i className="bi bi-plus-lg" aria-hidden="true" />
                          Add storage location
                        </Button>
                      </>
                    ) : null}
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer disabledReason={site ? disabledReason ?? undefined : undefined}>
          <Button variant="outline-secondary" onClick={onHide}>
            Close
          </Button>
          <Button
            variant="outline-primary"
            disabled={!site || countReason !== null}
            disabledReason={site ? countReason ?? undefined : undefined}
            onClick={() => setCounting(true)}
          >
            <i className="bi bi-clipboard-check" aria-hidden="true" />
            Start a count
          </Button>
          <Button
            variant="primary"
            disabled={!site || disabledReason !== null}
            disabledReason={disabledReason ?? undefined}
            onClick={() => openAdjust({ type: 'CONSUME', binId: null })}
          >
            <i className="bi bi-sliders" aria-hidden="true" />
            Adjust inventory
          </Button>
        </Modal.Footer>
      </Modal>

      {site && canManageStructure ? (
        <>
          <SiteFormModal
            show={structureForm?.kind === 'site'}
            site={site}
            onHide={() => setStructureForm(null)}
            onSaved={(saved, message) => {
              setStructureForm(null);
              setLoad({ status: 'ready', site: saved });
              setNotice({ variant: 'success', text: message });
              onChanged?.();
            }}
          />
          <StorageLocationFormModal
            show={structureForm?.kind === 'location'}
            site={site}
            location={structureForm?.kind === 'location' ? structureForm.location : null}
            onHide={() => setStructureForm(null)}
            onSaved={(message) => {
              setStructureForm(null);
              void refreshAfterSave(message);
            }}
          />
          <BinFormModal
            show={structureForm?.kind === 'bin'}
            site={site}
            locationId={structureForm?.kind === 'bin' ? structureForm.locationId : null}
            bin={structureForm?.kind === 'bin' ? structureForm.bin : null}
            canManageCost={canManageCost}
            onHide={() => setStructureForm(null)}
            onSaved={(message) => {
              setStructureForm(null);
              void refreshAfterSave(message);
            }}
          />
        </>
      ) : null}
      {site && canCount ? (
        <StartCountModal
          show={counting}
          site={{ id: site.id, name: site.name, number: site.number }}
          binCount={stockedBins}
          onHide={() => setCounting(false)}
        />
      ) : null}
      {site && canAdjust ? (
        <AdjustInventoryModal
          show={adjust !== null}
          site={site}
          initialType={adjust?.type ?? 'CONSUME'}
          initialBinId={adjust?.binId ?? null}
          canManageCost={canManageCost}
          onHide={() => setAdjust(null)}
          onSaved={(message) => {
            void refreshAfterSave(message);
          }}
        />
      ) : null}
      {site && canManageCertificates ? (
        <>
          <AttachCertificateModal
            show={attachFor !== null}
            site={site}
            binId={attachFor}
            onHide={() => setAttachFor(null)}
            onSaved={(message) => {
              void refreshCertificates(message);
            }}
          />
          <Modal show={removeTarget !== null} onHide={removing ? () => undefined : () => setRemoveTarget(null)}>
            <Modal.Header closeButton>
              <Modal.Title>Remove this certificate?</Modal.Title>
            </Modal.Header>
            <Modal.Body className="tw-space-y-3">
              <p className="tw-mb-0">
                {removeTarget ? certificateHeadline(removeTarget.description) : ''} will no longer be listed on {removeBinLabel}. If the
                same file is attached to other bins, it stays on them.
              </p>
              {removeError ? (
                <Alert variant="danger" className="tw-mb-0">
                  {removeError}
                </Alert>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setRemoveTarget(null)} disabled={removing}>
                Keep it
              </Button>
              <Button variant="danger" onClick={() => void confirmRemove()} disabled={removing}>
                {removing ? 'Removing…' : 'Remove certificate'}
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : null}
    </>
  );
};

export default SiteDetailModal;
